import { AttributeTextValue, CartLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import getExtensionPropertyValueByKey, { CommercePropertyValueEnum } from './getExtensionPropertyValueByKey';
import { SupersessionKey } from '../themes/gardner-theme/models/SupersessionKey';
import { ICartState } from '@msdyn365-commerce/global-state';
import { IWarehouseMapping, WarehouseInventoryType } from '../themes/gardner-theme/views/components/inventory-breakdown.component';
import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';

interface CheckAlternateWarehouseFeatureParams {
    extensionProperties: CommerceProperty[] | undefined;
    allowedDealerTypes: string[];
    designatedCustomerGroupsArr: string[];
    authorizedWarehouseAccounts: string[];
    isAlternateWarehouseFeatureEnabled: boolean;
    customerAccNum: string | undefined;
    isSobo: boolean;
    context: any;
}

export interface IUseWarehouseProps {
    cartLine: CartLine;
    cartState: ICartState | undefined;
    warehouse: WarehouseInventoryType | undefined;
    warehouses: WarehouseInventoryType[] | undefined;
    productExtensionProperties: CommerceProperty[] | undefined;
    appConfig: any;
    isAlternateWarehouseSelectionDisabled: boolean;
    stockLabel: string;
}

export interface AdditionalAltWarehouseDetails {
    CustomerGroup: string;
    FreightTermCodeId: string;
    ProductCode: string;
    GdnOverrideWhAutoSelect: boolean;
}

export type ShipmentType = 'FULL_SINGLE' | 'FULL_MULTI' | 'PARTIAL_SINGLE' | 'PARTIAL_MULTI' | 'FULL_BACKORDER';

export interface IShipmentInfo {
    type: ShipmentType;
    inStockQty: number;
    backorderQty: number;
    warehouseId?: string;
}

export interface WarehouseState {
    hasWarehouseChanged: boolean;
    isAlternateWarehouse: boolean;
    isWarehouseOverride: boolean;
    shouldDisplayAutoSourcedMessage: boolean;
    shouldUpdateWarehouse: boolean;
    totalStock: number;
    inStockLabel: string;
    shipmentInfo: IShipmentInfo;
    getWarehouseNameFromMappings: (wId?: string) => string | undefined;
    warehouses?: WarehouseInventoryType[] | undefined;
    warehouse?: WarehouseInventoryType | undefined;
}

export const getAdditionalAltWarehouseDetails = (extensionProperties: CommerceProperty[] | undefined) => {
    return getExtensionPropertyValueByKey(
        extensionProperties,
        SupersessionKey.AltWarehouseAdditional,
        CommercePropertyValueEnum.string
    ) as string;
};

export const checkAlternateWarehouseFeatureAccess = async ({
    extensionProperties,
    allowedDealerTypes,
    designatedCustomerGroupsArr,
    authorizedWarehouseAccounts,
    isAlternateWarehouseFeatureEnabled,
    customerAccNum,
    isSobo,
    context
}: CheckAlternateWarehouseFeatureParams): Promise<boolean> => {
    if (!isSobo && !customerAccNum) {
        return false;
    }

    const additionalAltWarehouseDetails = JSON.parse(
        getAdditionalAltWarehouseDetails(extensionProperties)
    ) as AdditionalAltWarehouseDetails;

    if (!additionalAltWarehouseDetails) {
        return false;
    }

    const freightTermCodeMet = allowedDealerTypes.includes(additionalAltWarehouseDetails.FreightTermCodeId);
    const customerGroupMet = designatedCustomerGroupsArr.includes(additionalAltWarehouseDetails.CustomerGroup);
    const isAuthorizedCustNum = typeof customerAccNum === 'string' && authorizedWarehouseAccounts.includes(customerAccNum);

    if (freightTermCodeMet && customerGroupMet && isAlternateWarehouseFeatureEnabled) {
        return true;
    }

    if (isAuthorizedCustNum) {
        return true;
    }

    if (isSobo) {
        const customer = await getCustomer(new GetCustomerInput(context.requestContext.apiSettings), context);
        if (authorizedWarehouseAccounts.includes(customer.AccountNumber)) {
            return true;
        }
    }

    return false;
};

/**
 * Converts a comma- or semicolon-separated string into an array of trimmed strings.
 * For example, "a, b; c" becomes ["a", "b", "c"].
 *
 * @param str - The comma- or semicolon-separated string.
 * @returns An array of trimmed string values.
 */
export const convertStringToArray = (str: string) => {
    return str?.split(/[;,]+/).map(ele => ele.trim()) ?? [];
};

export const getIsAlternateWarehouse = ({
    designatedDealerTypesArr,
    productCodeWhitelistArr,
    extensionProperties
}: {
    designatedDealerTypesArr: string[];
    productCodeWhitelistArr: string[];
    extensionProperties: CommerceProperty[] | undefined;
}) => {
    const additionalAltWarehouseDetails = JSON.parse(
        getAdditionalAltWarehouseDetails(extensionProperties)
    ) as AdditionalAltWarehouseDetails;
    if (!additionalAltWarehouseDetails) {
        return true;
    }

    if (designatedDealerTypesArr.includes(additionalAltWarehouseDetails.FreightTermCodeId)) {
        if (productCodeWhitelistArr.includes(additionalAltWarehouseDetails.ProductCode)) {
            return false;
        }
    }
    return true;
};

export const shouldEnableSecondaryWarehouses = (extensionProperties: CommerceProperty[] | undefined) => {
    const additionalAltWarehouseDetails = JSON.parse(
        getAdditionalAltWarehouseDetails(extensionProperties)
    ) as AdditionalAltWarehouseDetails;
    if (!additionalAltWarehouseDetails) {
        return false;
    }

    if (!additionalAltWarehouseDetails.GdnOverrideWhAutoSelect) {
        return true;
    }
    return false;
};

// export const updateWarehouse = async (warehouseId: string | undefined, cartLine: CartLine, cartState: ICartState | undefined) => {
//     let shouldRefresh = false;
//     if (!warehouseId || !cartLine || !cartState) {
//         return;
//     }
//     const warehouseAttr = cartLine?.AttributeValues?.find(attr => attr.Name === 'WarehouseId') as AttributeTextValue;
//     if (warehouseAttr && warehouseId === warehouseAttr?.TextValue) {
//         return;
//     }
//     const updatedExtensionProperties =
//         cartLine?.ExtensionProperties?.map(prop =>
//             prop.Key === 'HasWarehouseChanged' ? { ...prop, Value: { BooleanValue: false } } : prop
//         ) || cartLine?.ExtensionProperties;

//     cartLine = {
//         ...cartLine,
//         WarehouseId: warehouseId,
//         AttributeValues: [
//             {
//                 Name: 'WarehouseId',
//                 TextValue: warehouseId,
//                 '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue'
//             } as AttributeTextValue
//         ],
//         ExtensionProperties: [
//             ...(updatedExtensionProperties || []),
//             {
//                 Key: 'IsWarehouseOverrideByQuantity',
//                 Value: { BooleanValue: true }
//             }
//         ]
//     };

//     shouldRefresh = true;
//     try {
//         if (shouldRefresh) {
//             await cartState.updateCartLines({ updatedCartLinesObject: [cartLine] });
//             await cartState.refreshCart({});
//             shouldRefresh = false;
//         }
//     } catch (e) {
//         console.error('Cart update error:', e);
//     }
// };

// export const useWarehouseLogic = (props: IUseWarehouseProps) => {
//     const {
//         cartLine,
//         cartState,
//         warehouse,
//         warehouses,
//         productExtensionProperties,
//         appConfig,
//         stockLabel,
//         isAlternateWarehouseSelectionDisabled
//     } = props;

//     const warehouseMappings: IWarehouseMapping[] = appConfig.alternateWarehouseMappings || [];

//     const getWarehouseNameFromMappings = (warehouseId?: string) =>
//         warehouseMappings?.find(mapping => mapping.warehouseId === warehouseId)?.warehouseName;

//     const designatedDealerTypesArr = convertStringToArray(appConfig.designatedDealerTypes);
//     const productCodeWhitelistArr = convertStringToArray(appConfig.productCodeWhitelist);

//     const isAlternateWarehouse = getIsAlternateWarehouse({
//         designatedDealerTypesArr,
//         productCodeWhitelistArr,
//         extensionProperties: productExtensionProperties
//     });

//     const productQuantity = cartLine.Quantity ?? 0;
//     const totalStock = warehouses?.reduce((sum, w) => sum + (w.Available ?? 0), 0) ?? 0;
//     const anyWarehouseCanFulfill = warehouses?.some(w => (w.Available ?? 0) >= productQuantity) ?? false;
//     // const stockedWarehouses = warehouses?.filter(w => (w.Available ?? 0) > 0) ?? [];
//     const firstWarehouse = warehouses?.[0];
//     const enableSecondaryWarehouses = shouldEnableSecondaryWarehouses(productExtensionProperties);

//     const shipmentInfo = getShipmentInfo(productQuantity, totalStock, warehouses ?? [], enableSecondaryWarehouses, firstWarehouse);

//     const [shouldUpdateWarehouse, setShouldUpdateWarehouse] = React.useState(false);
//     const [isWarehouseOverride, setIsWarehouseOverride] = React.useState(false);
//     const [hasWarehouseChanged, setHasWarehouseChanged] = React.useState(false);
//     const [shouldDisplayAutoSourcedMessage, setShouldDisplayAutoSourcedMessage] = React.useState(false);
//     const [inStockLabel, setInStockLabel] = React.useState<string>(stockLabel);

//     const hasUpdatedRef = React.useRef(false);

//     React.useEffect(() => {
//         const override = getExtensionPropertyValueByKey(
//             cartLine.ExtensionProperties,
//             'IsWarehouseOverrideByQuantity',
//             CommercePropertyValueEnum.boolean
//         );
//         setIsWarehouseOverride(override as boolean);

//         const autoSource = getExtensionPropertyValueByKey(
//             cartLine.ExtensionProperties,
//             'shouldDisplayAutoSourcedMessage',
//             CommercePropertyValueEnum.boolean
//         );
//         setShouldDisplayAutoSourcedMessage(autoSource as boolean);

//         const hasWarehouseChangedpProp = getExtensionPropertyValueByKey(
//             cartLine.ExtensionProperties,
//             'HasWarehouseChanged',
//             CommercePropertyValueEnum.boolean
//         );

//         setHasWarehouseChanged(hasWarehouseChangedpProp as boolean);
//     }, [cartLine.ExtensionProperties]);

//     React.useEffect(() => {
//         const isPrimaryWh = warehouse?.WarehouseId === firstWarehouse?.WarehouseId;
//         const primaryHasInsufficientStock = productQuantity > (firstWarehouse?.Available ?? 0);
//         const shouldAutoSourceBecauseSingleSupplier =
//             shipmentInfo.type === 'FULL_SINGLE' &&
//             Boolean(shipmentInfo.warehouseId) &&
//             shipmentInfo.warehouseId !== firstWarehouse?.WarehouseId;

//         const shouldUpdate =
//             enableSecondaryWarehouses && isPrimaryWh && primaryHasInsufficientStock && shouldAutoSourceBecauseSingleSupplier;

//         setShouldUpdateWarehouse(shouldUpdate);
//         setInStockLabel(
//             !isAlternateWarehouseSelectionDisabled
//                 ? !enableSecondaryWarehouses
//                     ? productQuantity > (warehouse?.Available ?? 0)
//                         ? `${warehouse?.Available ?? 0} ${stockLabel}`
//                         : stockLabel
//                     : productQuantity > totalStock
//                     ? `${totalStock} ${stockLabel}`
//                     : stockLabel
//                 : stockLabel
//         );
//     }, [
//         productExtensionProperties,
//         productQuantity,
//         warehouse,
//         warehouses,
//         shipmentInfo,
//         enableSecondaryWarehouses,
//         anyWarehouseCanFulfill,
//         totalStock,
//         isAlternateWarehouseSelectionDisabled,
//         stockLabel
//     ]);

//     React.useEffect(() => {
//         hasUpdatedRef.current = false;
//     }, [productQuantity, isWarehouseOverride, shipmentInfo.warehouseId]);

//     React.useEffect(() => {
//         if (shouldUpdateWarehouse && !isWarehouseOverride && !hasUpdatedRef.current) {
//             if (shipmentInfo?.warehouseId) {
//                 updateWarehouse(shipmentInfo.warehouseId, cartLine, cartState);
//                 hasUpdatedRef.current = true;
//             }
//         }
//     }, [shouldUpdateWarehouse, isWarehouseOverride, shipmentInfo, cartLine, cartState]);

//     return {
//         hasWarehouseChanged,
//         getWarehouseNameFromMappings,
//         isAlternateWarehouse,
//         shouldUpdateWarehouse,
//         isWarehouseOverride,
//         shouldDisplayAutoSourcedMessage,
//         warehouse,
//         warehouses,
//         totalStock,
//         inStockLabel,
//         shipmentInfo
//     };
// };

const getShipmentInfo = (
    productQuantity: number,
    totalStock: number,
    warehouses: WarehouseInventoryType[],
    enableSecondaryWarehouses: boolean,
    primaryWarehouse: WarehouseInventoryType | undefined
): IShipmentInfo => {
    // Edge: no stock at all -> everything backordered.
    if (totalStock === 0) {
        return { type: 'FULL_BACKORDER', inStockQty: 0, backorderQty: productQuantity };
    }

    // If disable seconday warehouse and the "primary" warehouse is the first
    // warehouse, keep using the primary warehouse only (existing behaviour).
    if (!enableSecondaryWarehouses && primaryWarehouse?.WarehouseId === warehouses[0]?.WarehouseId) {
        if ((primaryWarehouse?.Available ?? 0) >= productQuantity) {
            return {
                type: 'FULL_SINGLE',
                inStockQty: productQuantity,
                warehouseId: primaryWarehouse?.WarehouseId ?? '',
                backorderQty: 0
            };
        } else {
            return {
                type: 'PARTIAL_SINGLE',
                inStockQty: primaryWarehouse?.Available ?? 0,
                backorderQty: Math.max(productQuantity - (primaryWarehouse?.Available ?? 0), 0),
                warehouseId: primaryWarehouse?.WarehouseId ?? ''
            };
        }
    }

    const backorderQty = Math.max(productQuantity - totalStock, 0);

    let remaining = productQuantity;
    const allocation: { warehouseId?: string; qty: number; name?: string }[] = [];

    for (const w of warehouses) {
        const avail = w.Available ?? 0;
        if (avail <= 0) {
            continue;
        }
        const take = Math.min(avail, remaining);
        if (take > 0) {
            allocation.push({ warehouseId: w.WarehouseId, qty: take, name: w.Name });
            remaining -= take;
        }
        if (remaining <= 0) {
            break;
        }
    }

    const usedCount = allocation.length;
    const inStockQty = productQuantity - backorderQty;

    if (remaining <= 0) {
        if (usedCount === 1) {
            return {
                type: 'FULL_SINGLE',
                inStockQty: productQuantity,
                backorderQty: 0,
                warehouseId: allocation[0].warehouseId ?? ''
            };
        }
        return {
            type: 'FULL_MULTI',
            inStockQty: productQuantity,
            backorderQty: 0
        };
    }

    if (usedCount === 1) {
        return {
            type: 'PARTIAL_SINGLE',
            inStockQty: allocation[0].qty,
            backorderQty,
            warehouseId: allocation[0].warehouseId ?? ''
        };
    }

    return {
        type: 'PARTIAL_MULTI',
        inStockQty,
        backorderQty
    };
};

export const computeWarehouseStateForLine = (
    cartLine: CartLine,
    warehouses: WarehouseInventoryType[] | undefined,
    productExtensionProperties: CommerceProperty[] | undefined,
    appConfig: any,
    stockLabel: string,
    isAlternateWarehouseSelectionDisabled: boolean
): WarehouseState => {
    const warehouseMappings: IWarehouseMapping[] = appConfig.alternateWarehouseMappings || [];
    const warehouse =
        warehouses?.find(
            w => w.WarehouseId === (cartLine.AttributeValues?.find(a => a.Name === 'WarehouseId') as AttributeTextValue)?.TextValue
        ) || warehouses?.[0];
    const getWarehouseNameFromMappings = (warehouseId?: string) =>
        warehouseMappings?.find(mapping => mapping.warehouseId === warehouseId)?.warehouseName;

    const designatedDealerTypesArr = convertStringToArray(appConfig.designatedDealerTypes);
    const productCodeWhitelistArr = convertStringToArray(appConfig.productCodeWhitelist);

    const isAlternateWarehouse = getIsAlternateWarehouse({
        designatedDealerTypesArr,
        productCodeWhitelistArr,
        extensionProperties: productExtensionProperties
    });

    const productQuantity = cartLine?.Quantity ?? 0;
    const totalStock = warehouses?.reduce((sum, w) => sum + (w.Available ?? 0), 0) ?? 0;
    const firstWarehouse = warehouses?.[0];
    const enableSecondaryWarehouses = shouldEnableSecondaryWarehouses(productExtensionProperties);

    const shipmentInfo = getShipmentInfo(productQuantity, totalStock, warehouses ?? [], enableSecondaryWarehouses, firstWarehouse);

    const override = getExtensionPropertyValueByKey(
        cartLine.ExtensionProperties,
        'IsWarehouseOverrideByQuantity',
        CommercePropertyValueEnum.boolean
    ) as boolean;
    const autoSource = getExtensionPropertyValueByKey(
        cartLine.ExtensionProperties,
        'shouldDisplayAutoSourcedMessage',
        CommercePropertyValueEnum.boolean
    ) as boolean;
    const hasWarehouseChangedProp = getExtensionPropertyValueByKey(
        cartLine.ExtensionProperties,
        'HasWarehouseChanged',
        CommercePropertyValueEnum.boolean
    ) as boolean;

    const isWarehouseOverride = Boolean(override);
    const shouldDisplayAutoSourcedMessage = Boolean(autoSource);
    const hasWarehouseChanged = Boolean(hasWarehouseChangedProp);

    const isPrimaryWh = warehouse?.WarehouseId === firstWarehouse?.WarehouseId;
    const primaryHasInsufficientStock = productQuantity > (firstWarehouse?.Available ?? 0);
    const shouldAutoSourceBecauseSingleSupplier =
        shipmentInfo.type === 'FULL_SINGLE' &&
        Boolean(shipmentInfo.warehouseId) &&
        shipmentInfo.warehouseId !== firstWarehouse?.WarehouseId;

    const shouldUpdateWarehouse =
        enableSecondaryWarehouses && isPrimaryWh && primaryHasInsufficientStock && shouldAutoSourceBecauseSingleSupplier;

    const inStockLabel = !isAlternateWarehouseSelectionDisabled
        ? !enableSecondaryWarehouses
            ? productQuantity > (warehouse?.Available ?? 0)
                ? `${warehouse?.Available ?? 0} ${stockLabel}`
                : stockLabel
            : productQuantity > totalStock
            ? `${totalStock} ${stockLabel}`
            : stockLabel
        : stockLabel;

    return {
        hasWarehouseChanged,
        isAlternateWarehouse,
        isWarehouseOverride,
        shouldDisplayAutoSourcedMessage,
        shouldUpdateWarehouse,
        totalStock,
        inStockLabel,
        shipmentInfo,
        getWarehouseNameFromMappings,
        warehouses,
        warehouse
    };
};

export const buildUpdatedCartLine = (cartLine: CartLine, warehouseId: string): CartLine => {
    const updatedExtensionProperties =
        cartLine?.ExtensionProperties?.map(prop =>
            prop.Key === 'HasWarehouseChanged' ? { ...prop, Value: { BooleanValue: false } } : prop
        ) || cartLine?.ExtensionProperties;

    return {
        ...cartLine,
        WarehouseId: warehouseId,
        AttributeValues: [
            {
                Name: 'WarehouseId',
                TextValue: warehouseId,
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue'
            } as AttributeTextValue
        ],
        ExtensionProperties: [
            ...(updatedExtensionProperties || []),
            {
                Key: 'IsWarehouseOverrideByQuantity',
                Value: { BooleanValue: true }
            }
        ]
    };
};
